import React, { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Sparkles, Shuffle, RotateCcw, BookmarkPlus, ArrowLeft, Wand2, Check, Sun, Share2 } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DeckSelector } from "@/components/DeckSelector";
import { SpreadPicker } from "@/components/SpreadPicker";
import { TarotCard } from "@/components/TarotCard";
import { ReadingText } from "@/components/ReadingText";
import { ShareCard } from "@/components/ShareCard";
import { generateShareImage } from "@/lib/shareCard";
import { getTodayDraw } from "@/lib/daily";
import { UpgradeModal } from "@/components/UpgradeModal";
import { Crown } from "lucide-react";
import { HowItWorks } from "@/components/home/HowItWorks";
import { ExampleReading } from "@/components/home/ExampleReading";
import { Faq } from "@/components/home/Faq";
import { Testimonials, ThirdPartyStrip } from "@/components/home/Proof";
import { FREE_PROMISE, SIGNED_IN_PROMISE, PRIVACY_LINE } from "@/content/site";
import { usePageMeta } from "@/hooks/usePageMeta";
import { savePendingReading, takePendingReading, clearPendingReading, hasPendingReading } from "@/lib/pendingReading";
import { track, planOf } from "@/lib/analytics";

export default function Home() {
  const { user, loading: authLoading, login, refreshEntitlements, entitlements } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const promise = !user || !entitlements
    ? FREE_PROMISE
    : entitlements.is_premium
      ? SIGNED_IN_PROMISE.premium
      : SIGNED_IN_PROMISE.free(entitlements.complimentary_remaining ?? 0);
  usePageMeta({
    title: "The Cardlight - AI Tarot & Oracle Card Readings Online",
    description: "Write the exact question on your mind, draw tarot or oracle cards, and get an AI reading about it. First 3 readings free, no card required.",
    path: "/",
  });

  const dailyDone = getTodayDraw();
  // The single-card "daily" spread is owned by the dedicated Daily Ritual page.
  const mainSpreads = (d) => (d?.spreads || []).filter((s) => s.id !== "daily");
  const [decks, setDecks] = useState([]);
  const [deckId, setDeckId] = useState(null);
  const [spreadId, setSpreadId] = useState(null);
  const [question, setQuestion] = useState("");
  const [allowReversals, setAllowReversals] = useState(true);

  const [step, setStep] = useState("setup"); // setup | shuffle | reveal | reading
  const [draw, setDraw] = useState(null);
  const [flipped, setFlipped] = useState([]);
  const [interpretation, setInterpretation] = useState("");
  const [interpreting, setInterpreting] = useState(false);
  const [saved, setSaved] = useState(false);
  const startedInterpret = useRef(false);
  const [limitReached, setLimitReached] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const shareRef = useRef(null);
  const [sharing, setSharing] = useState(false);

  const handleShare = async () => {
    if (!shareRef.current) return;
    setSharing(true);
    try {
      const res = await generateShareImage(shareRef.current, "the-cardlight-reading.png");
      toast.success(res === "shared" ? "Reading shared ✦" : "Reading image saved ✦");
    } catch (e) {
      toast.error("Could not create the share image.");
    } finally {
      setSharing(false);
    }
  };

  const deck = decks.find((d) => d.id === deckId);
  const spread = deck?.spreads.find((s) => s.id === spreadId);

  useEffect(() => {
    api
      .get("/decks")
      .then((res) => {
        setDecks(res.data);
        if (res.data.length) {
          setDeckId(res.data[0].id);
          setSpreadId(mainSpreads(res.data[0])[0]?.id || null);
        }
      })
      .catch(() => toast.error("Could not load the decks."));
  }, []);

  useEffect(() => {
    if (location.hash === "#faq") document.getElementById("faq")?.scrollIntoView({ block: "start" });
  }, [location.hash, decks.length]);

  useEffect(() => {
    if (!user) {
      if (!authLoading && hasPendingReading()) clearPendingReading();
      return;
    }
    const pending = takePendingReading();
    if (!pending) return;
    setDeckId(pending.deck.id);
    setSpreadId(pending.spread.id);
    setQuestion(pending.question || "");
    setDraw(pending);
    setFlipped(new Array(pending.cards.length).fill(true));
    setInterpretation("");
    setSaved(false);
    startedInterpret.current = false;
    setStep("reveal");
    toast.success("Your cards are waiting. Reveal your reading when you're ready.");
    setTimeout(() => document.querySelector('[data-testid="reveal-reading-btn"]')?.scrollIntoView({ behavior: "smooth", block: "center" }), 600);
  }, [user, authLoading]);

  const selectDeck = (id) => {
    setDeckId(id);
    const d = decks.find((x) => x.id === id);
    setSpreadId(mainSpreads(d)[0]?.id || null);
  };

  const beginReading = async () => {
    if (!deckId || !spreadId) return;
    clearPendingReading();
    setStep("shuffle");
    setInterpretation("");
    setSaved(false);
    startedInterpret.current = false;
    try {
      const res = await api.post("/readings/draw", {
        deck_id: deckId,
        spread_id: spreadId,
        allow_reversals: allowReversals,
        question: question || null,
      });
      setDraw(res.data);
      setFlipped(new Array(res.data.cards.length).fill(false));
      track("reading_started", {
        deck_id: res.data.deck.id,
        spread_id: res.data.spread.id,
        card_count: res.data.cards.length,
        has_question: Boolean(question),
      });
      setTimeout(() => setStep("reveal"), 1900);
    } catch (e) {
      toast.error("The cards resisted the draw. Try again.");
      setStep("setup");
    }
  };

  const flipCard = (i) => {
    setFlipped((prev) => {
      if (prev[i]) return prev;
      const next = [...prev];
      next[i] = true;
      return next;
    });
  };

  const allFlipped = flipped.length > 0 && flipped.every(Boolean);

  const revealReading = async () => {
    if (startedInterpret.current) return;
    startedInterpret.current = true;
    setStep("reading");
    setInterpreting(true);
    setInterpretation("");

    const payload = {
      deck_id: draw.deck.id,
      spread_id: draw.spread.id,
      question: draw.question || null,
      cards: draw.cards.map((c) => ({
        position: c.position,
        card_id: c.card.id,
        reversed: c.reversed,
      })),
    };

    try {
      const res = await fetch(`${API}/readings/interpret`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      if (res.status === 401) {
        toast.info("Sign in to receive your reading.");
        savePendingReading(draw);
        login();
        setInterpreting(false);
        return;
      }
      if (res.status === 402) {
        const data = await res.json().catch(() => ({}));
        const premium = String(data.detail || "").includes("premium");
        toast.error(premium
          ? "You've used all 12 readings for this month."
          : "You've used your 3 complimentary readings. Upgrade for 12 readings a month.");
        setInterpreting(false);
        setLimitReached(true);
        track("reading_limit_reached", { plan: premium ? "premium" : "free" });
        if (!premium) setUpgradeOpen(true);
        return;
      }
      if (res.status === 429) {
        const data = await res.json().catch(() => ({}));
        toast.error(data.detail || "You've reached the reading limit for now. Please try again later.");
        setInterpreting(false);
        return;
      }
      if (!res.body) throw new Error("No stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let received = false;
      let failed = false;
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buffer.indexOf("\n\n")) >= 0) {
          const chunk = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          const line = chunk.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          try {
            const payload2 = JSON.parse(line.slice(5).trim());
            if (payload2.t) { received = true; setInterpretation((p) => p + payload2.t); }
            if (payload2.error) { failed = true; toast.error("The reading faltered midway."); }
          } catch (err) {
            console.debug("Skipping unparseable SSE chunk", err);
          }
        }
      }
      if (received && !failed) {
        track("reading_completed", {
          deck_id: draw.deck.id,
          spread_id: draw.spread.id,
          card_count: draw.cards.length,
          plan: planOf(entitlements),
        });
      }
    } catch (e) {
      toast.error("Could not channel the reading.");
    } finally {
      setInterpreting(false);
      if (user) refreshEntitlements();
    }
  };

  const saveReading = async () => {
    if (!user) {
      toast.info("Sign in to keep this reading in your journal.");
      login();
      return;
    }
    try {
      await api.post("/readings", {
        deck_id: draw.deck.id,
        spread_id: draw.spread.id,
        question: draw.question || null,
        cards: draw.cards.map((c) => ({
          position: c.position,
          card_id: c.card.id,
          reversed: c.reversed,
        })),
        interpretation,
      });
      setSaved(true);
      toast.success("Saved to your journal ✦");
    } catch (e) {
      toast.error("Could not save this reading.");
    }
  };

  const reset = () => {
    clearPendingReading();
    setStep("setup");
    setDraw(null);
    setFlipped([]);
    setInterpretation("");
    setSaved(false);
    startedInterpret.current = false;
  };

  // ----------------------------- SETUP -----------------------------
  if (step === "setup") {
    return (
      <div className="mx-auto max-w-5xl px-5 py-10 sm:px-8 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-[#D9A441]/30 bg-[#131422]/70 px-4 py-1.5 text-xs uppercase tracking-[0.25em] text-[#E5C365]">
            <Sparkles className="h-3.5 w-3.5" /> a quiet moment of insight
          </span>
          <h1 className="font-display text-5xl font-semibold leading-[1.05] tracking-tight text-[#F8F3E6] sm:text-6xl lg:text-7xl">
            Draw the cards.
            <br />
            <span className="text-[#D9A441]">Hear their whisper.</span>
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-lg leading-relaxed text-[#F8F3E6]" data-testid="hero-subhead">
            AI tarot and oracle readings, in a quiet moment of your own.
          </p>
          <p className="mx-auto mt-2 max-w-xl text-base leading-relaxed text-[#A3A5B8]">
            For when you're stuck on a decision and want a reading about your exact question —
            built from the cards you draw, not a generic paragraph.
          </p>
        </motion.div>

        <motion.button
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          onClick={() => navigate("/daily")}
          data-testid="home-daily-cta"
          className="mb-10 flex w-full items-center justify-between gap-4 rounded-2xl border border-[#D9A441]/25 bg-gradient-to-r from-[#131422] to-[#1a1426] px-6 py-4 text-left transition-all hover:border-[#D9A441]/50 hover:shadow-[0_0_24px_rgba(217,164,65,0.15)]"
        >
          <div className="flex items-center gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#D9A441]/40 bg-[#0B0C15]/60">
              <Sun className="h-5 w-5 text-[#D9A441]" />
            </span>
            <div>
              <p className="font-display text-xl font-medium text-[#F8F3E6]">Daily Ritual</p>
              <p className="text-sm text-[#A3A5B8]">
                {dailyDone
                  ? "You've drawn today's card — revisit its message."
                  : "Pull your single card of the day for a quiet moment of guidance."}
              </p>
              <p className="mt-0.5 text-xs text-[#E5C365]/80" data-testid="daily-free-note">
                Free with sign-in · never uses one of your readings
              </p>
            </div>
          </div>
          <span className="hidden shrink-0 items-center gap-2 rounded-full border border-[#D9A441]/50 px-4 py-2 text-sm text-[#E5C365] sm:flex">
            {dailyDone ? "View card" : "Draw today"}
            {!dailyDone && (
              <span className="h-2 w-2 rounded-full bg-[#D9A441]" style={{ boxShadow: "0 0 8px rgba(217,164,65,0.8)" }} />
            )}
          </span>
        </motion.button>

        <HowItWorks />
        <ExampleReading />

        <section className="mb-10">
          <SectionLabel step="I" title="Choose your deck" />
          <DeckSelector decks={decks} selectedId={deckId} onSelect={selectDeck} />
        </section>

        {deck && (
          <section className="mb-10">
            <SectionLabel step="II" title="Choose a spread" />
            <SpreadPicker spreads={mainSpreads(deck)} selectedId={spreadId} onSelect={setSpreadId} />
          </section>
        )}

        <section className="mb-10">
          <SectionLabel step="III" title="Focus your question" optional />
          <Textarea
            data-testid="question-input"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="What would you like clarity on? (optional)"
            className="ph-no-capture min-h-[92px] resize-none border-[#D9A441]/25 bg-[#131422]/70 text-[#F8F3E6] placeholder:text-[#A3A5B8]/60 focus-visible:ring-[#D9A441]"
          />
          {deck?.supports_reversals && (
            <div className="mt-5 flex items-center justify-between rounded-xl border border-[#D9A441]/15 bg-[#131422]/50 px-5 py-4">
              <div>
                <p className="font-medium text-[#F8F3E6]">Allow reversed cards</p>
                <p className="text-sm text-[#A3A5B8]">Reversed cards add shadow and nuance.</p>
              </div>
              <Switch
                data-testid="reversals-toggle"
                checked={allowReversals}
                onCheckedChange={setAllowReversals}
              />
            </div>
          )}
        </section>

        <motion.button
          whileHover={{ scale: 1.015 }}
          whileTap={{ scale: 0.985 }}
          onClick={beginReading}
          disabled={!deckId || !spreadId}
          data-testid="begin-reading-btn"
          className="group flex w-full items-center justify-center gap-3 rounded-full border border-[#D9A441]/60 bg-[#D9A441] px-8 py-4 font-medium text-[#0B0C15] transition-all hover:bg-[#E5C365] hover:shadow-[0_0_30px_rgba(217,164,65,0.4)] disabled:opacity-40"
        >
          <Shuffle className="h-5 w-5" />
          Begin the reading
        </motion.button>
        <div className="mt-4 space-y-1.5 text-center" data-testid="cta-reassurance">
          <p className="text-sm text-[#F8F3E6]" data-testid="cta-free-promise">{promise}</p>
          <p className="mx-auto max-w-xl text-xs leading-relaxed text-[#A3A5B8]" data-testid="cta-privacy-line">{PRIVACY_LINE}</p>
          <p className="pt-1 text-xs text-[#A3A5B8]">
            <Link to="/privacy" data-testid="cta-privacy-link" className="text-[#E5C365] underline underline-offset-4 hover:text-[#F8F3E6]">How your data is handled</Link>
            {" · "}Not ready yet?{" "}
            <Link to="/pricing" data-testid="cta-pricing-link" className="text-[#E5C365] underline underline-offset-4 hover:text-[#F8F3E6]">See pricing</Link>
            {" · "}
            <a href="#faq" data-testid="cta-faq-link" className="text-[#E5C365] underline underline-offset-4 hover:text-[#F8F3E6]">Read the FAQ</a>
            {" · "}
            <a href="#how-heading" data-testid="cta-how-link" className="text-[#E5C365] underline underline-offset-4 hover:text-[#F8F3E6]">How it works</a>
          </p>
        </div>
        <Testimonials />
        <ThirdPartyStrip />
        <Faq />
      </div>
    );
  }

  // ----------------------------- SHUFFLE -----------------------------
  if (step === "shuffle") {
    return (
      <div className="flex min-h-[70vh] flex-col items-center justify-center px-6" data-testid="shuffle-view">
        <div className="relative h-56 w-40">
          {[0, 1, 2, 3, 4].map((i) => (
            <motion.div
              key={i}
              className="absolute inset-0 rounded-xl border border-[#D9A441]/50 bg-[#131422]"
              animate={{
                rotate: [0, i % 2 === 0 ? -14 : 14, 0],
                x: [0, i % 2 === 0 ? -30 : 30, 0],
                y: [0, -12, 0],
              }}
              transition={{ duration: 1.4, repeat: Infinity, delay: i * 0.12, ease: "easeInOut" }}
              style={{ zIndex: i }}
            >
              <div className="flex h-full w-full items-center justify-center">
                <Sparkles className="h-7 w-7 text-[#D9A441]/70" />
              </div>
            </motion.div>
          ))}
        </div>
        <p className="mt-10 font-display text-2xl text-[#F8F3E6]">Shuffling the deck…</p>
        <p className="mt-1 text-sm text-[#A3A5B8]">Hold your question gently in mind.</p>
      </div>
    );
  }

  // ----------------------------- REVEAL / READING -----------------------------
  return (
    <div className="ph-no-capture mx-auto max-w-5xl px-5 py-10 sm:px-8" data-testid="reading-view">
      <div className="mb-8 flex items-center justify-between">
        <button
          onClick={reset}
          data-testid="new-reading-btn"
          className="flex items-center gap-2 text-sm text-[#A3A5B8] transition-colors hover:text-[#F8F3E6]"
        >
          <ArrowLeft className="h-4 w-4" /> New reading
        </button>
        <div className="text-right">
          <p className="font-display text-xl text-[#F8F3E6]">{draw?.spread.name}</p>
          <p className="text-xs uppercase tracking-[0.2em] text-[#A3A5B8]">{draw?.deck.name}</p>
        </div>
      </div>

      {draw?.question && (
        <p className="mb-8 text-center font-display text-xl italic text-[#E5C365]">
          “{draw.question}”
        </p>
      )}

      <div
        className="mb-8 flex flex-wrap items-start justify-center gap-6"
        style={{ perspective: 1400 }}
        data-testid="reading-table"
      >
        {draw?.cards.map((c, i) => (
          <div key={i} className="flex w-40 flex-col items-center gap-3 sm:w-48 md:w-52">
            <span className="text-xs uppercase tracking-[0.25em] text-[#A3A5B8]">{c.position}</span>
            <TarotCard
              card={c.card}
              reversed={c.reversed}
              flipped={flipped[i]}
              deckType={draw.deck.type}
              onClick={step === "reveal" && !flipped[i] ? () => flipCard(i) : undefined}
              index={i}
              testId={`reading-card-${i}`}
            />
            {flipped[i] && draw.deck.type === "oracle" && (
              <motion.div
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
                className="w-full text-center"
                data-testid={`card-caption-${i}`}
              >
                <div className="mb-1.5 flex flex-wrap justify-center gap-1">
                  {(c.card.keywords || []).map((k) => (
                    <span
                      key={k}
                      className="rounded-full border border-[#D9A441]/25 bg-[#131422]/70 px-2 py-0.5 text-[10px] text-[#E5C365]"
                    >
                      {k}
                    </span>
                  ))}
                </div>
                <p className="text-xs leading-relaxed text-[#A3A5B8]">{c.card.extended_meaning}</p>
                {c.card.journal_prompt && (
                  <p className="mt-2 font-display text-sm italic leading-relaxed text-[#E5C365]/90">
                    “{c.card.journal_prompt}”
                  </p>
                )}
              </motion.div>
            )}
          </div>
        ))}
      </div>

      {step === "reveal" && (
        <div className="flex flex-col items-center gap-3">
          {!allFlipped ? (
            <p className="text-sm text-[#A3A5B8]" data-testid="flip-hint">
              Tap each card to reveal it ({flipped.filter(Boolean).length}/{flipped.length})
            </p>
          ) : (
            <motion.button
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              onClick={revealReading}
              data-testid="reveal-reading-btn"
              className="flex items-center gap-3 rounded-full border border-[#D9A441]/60 bg-[#D9A441] px-8 py-3.5 font-medium text-[#0B0C15] transition-all hover:bg-[#E5C365] hover:shadow-[0_0_28px_rgba(217,164,65,0.4)]"
            >
              <Wand2 className="h-5 w-5" /> Reveal your reading
            </motion.button>
          )}
        </div>
      )}

      <AnimatePresence>
        {step === "reading" && (
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mx-auto mt-4 max-w-3xl rounded-2xl border border-[#D9A441]/25 bg-[#131422]/80 p-7 backdrop-blur-xl sm:p-9"
            data-testid="reading-panel"
          >
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-[#D9A441]/40 bg-[#0B0C15]/60">
                <Sparkles className="h-4 w-4 text-[#D9A441]" />
              </span>
              <h2 className="font-display text-2xl font-semibold text-[#F8F3E6]">Your Reading</h2>
            </div>

            <ReadingText
              text={interpretation}
              streaming={interpreting}
              testId="reading-text"
            />

            {limitReached && !interpretation && (
              <div
                className="flex flex-col items-center rounded-2xl border border-[#D9A441]/25 bg-[#0B0C15]/40 px-6 py-8 text-center"
                data-testid="limit-reached-panel"
              >
                <span className="flex h-12 w-12 items-center justify-center rounded-full border border-[#D9A441]/50 bg-[#131422]">
                  <Crown className="h-5 w-5 text-[#D9A441]" />
                </span>
                <h3 className="mt-5 font-display text-2xl text-[#F8F3E6]">
                  You've reached your reading limit
                </h3>
                <p className="mt-2 max-w-md text-sm text-[#A3A5B8]">
                  Upgrade to The Cardlight Premium for 12 full readings every month — plus
                  a Daily Ritual and up to 3 follow-up questions per reading.
                </p>
                <button
                  onClick={() => setUpgradeOpen(true)}
                  data-testid="limit-upgrade-btn"
                  className="mt-6 flex items-center gap-2 rounded-full border border-[#D9A441]/60 bg-[#D9A441] px-7 py-3 font-medium text-[#0B0C15] transition-all hover:bg-[#E5C365] hover:shadow-[0_0_28px_rgba(217,164,65,0.4)]"
                >
                  <Sparkles className="h-4 w-4" /> Upgrade to Premium
                </button>
              </div>
            )}

            {!interpreting && interpretation && (
              <div className="mt-8 flex flex-wrap items-center gap-3 border-t border-[#D9A441]/15 pt-6">
                <button
                  onClick={saveReading}
                  disabled={saved}
                  data-testid="save-reading-btn"
                  className="flex items-center gap-2 rounded-full border border-[#D9A441]/50 bg-[#D9A441]/10 px-5 py-2.5 text-sm font-medium text-[#E5C365] transition-all hover:bg-[#D9A441]/20 disabled:opacity-60"
                >
                  {saved ? <Check className="h-4 w-4" /> : <BookmarkPlus className="h-4 w-4" />}
                  {saved ? "Saved to journal" : user ? "Save to journal" : "Sign in to save"}
                </button>
                <button
                  onClick={reset}
                  data-testid="draw-again-btn"
                  className="flex items-center gap-2 rounded-full px-5 py-2.5 text-sm text-[#A3A5B8] transition-colors hover:text-[#F8F3E6]"
                >
                  <RotateCcw className="h-4 w-4" /> Draw again
                </button>
                <button
                  onClick={handleShare}
                  disabled={sharing}
                  data-testid="share-reading-btn"
                  className="flex items-center gap-2 rounded-full border border-[#D9A441]/30 px-5 py-2.5 text-sm text-[#E5C365] transition-colors hover:border-[#D9A441]/60 hover:bg-[#D9A441]/10 disabled:opacity-60"
                >
                  <Share2 className="h-4 w-4" /> {sharing ? "Creating…" : "Share"}
                </button>
              </div>
            )}
            {draw && (
              <ShareCard
                ref={shareRef}
                deckName={draw.deck?.name}
                spreadName={draw.spread?.name}
                cards={draw.cards}
                interpretation={interpretation}
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>
      <UpgradeModal open={upgradeOpen} onOpenChange={setUpgradeOpen} />
    </div>
  );
}

const SectionLabel = ({ step, title, optional }) => (
  <div className="mb-4 flex items-center gap-3">
    <span className="flex h-7 w-7 items-center justify-center rounded-full border border-[#D9A441]/40 font-display text-sm text-[#D9A441]">
      {step}
    </span>
    <h2 className="font-display text-2xl font-medium text-[#F8F3E6]">{title}</h2>
    {optional && <span className="text-xs uppercase tracking-widest text-[#A3A5B8]">optional</span>}
  </div>
);
